import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminReports.scss';

const DEFAULT_FILTERS = {
  targetType: 'all',
  status: 'pending'
};

export default function AdminReports() {
  const navigate = useNavigate();

  const apiBaseUrl = useMemo(() => {
    const envUrl = process.env.REACT_APP_API_URL || '';
    return envUrl.endsWith('/') ? envUrl.slice(0, -1) : envUrl;
  }, []);

  const token = localStorage.getItem('tg_admin_token');
  const storedUser = localStorage.getItem('tg_admin_user');

  const [adminUser, setAdminUser] = useState(storedUser ? JSON.parse(storedUser) : null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [targetContent, setTargetContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [panelLoading, setPanelLoading] = useState(false);
  const [error, setError] = useState('');
  const [saveStatus, setSaveStatus] = useState({
    loading: false,
    success: '',
    error: ''
  });

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }

    const validateAdmin = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/admin/me`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.message || 'Session expired.');
        }

        localStorage.setItem('tg_admin_user', JSON.stringify(data.adminUser));
        setAdminUser(data.adminUser);
      } catch (err) {
        localStorage.removeItem('tg_admin_token');
        localStorage.removeItem('tg_admin_user');
        navigate('/admin');
      }
    };

    validateAdmin();
  }, [apiBaseUrl, navigate, token]);

  useEffect(() => {
    if (!token) {
      return;
    }

    const fetchReports = async () => {
      setLoading(true);
      setError('');

      try {
        const params = new URLSearchParams();

        if (filters.targetType !== 'all') {
          params.append('targetType', filters.targetType);
        }

        if (filters.status !== 'all') {
          params.append('status', filters.status);
        }

        params.append('limit', '100');

        const response = await fetch(`${apiBaseUrl}/admin/reports?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.message || 'Unable to load reports.');
        }

        setReports(data.reports || []);

        if (data.reports && data.reports.length > 0 && !selectedReport) {
          await handleSelectReport(data.reports[0].report_id);
        }

        if (data.reports && data.reports.length === 0) {
          setSelectedReport(null);
          setTargetContent(null);
        }
      } catch (err) {
        setError(err.message || 'Something went wrong loading reports.');
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, filters, token]);

  const handleLogout = () => {
    localStorage.removeItem('tg_admin_token');
    localStorage.removeItem('tg_admin_user');
    navigate('/admin');
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value
    }));

    setSelectedReport(null);
    setTargetContent(null);
  };

  const handleSelectReport = async (reportId) => {
    setPanelLoading(true);
    setSaveStatus({
      loading: false,
      success: '',
      error: ''
    });

    try {
      const response = await fetch(`${apiBaseUrl}/admin/reports/${reportId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || 'Unable to load report.');
      }

      setSelectedReport(data.report);
      setTargetContent(data.targetContent);
    } catch (err) {
      setSaveStatus({
        loading: false,
        success: '',
        error: err.message || 'Unable to load report details.'
      });
    } finally {
      setPanelLoading(false);
    }
  };

  const handleSelectedFieldChange = (event) => {
    const { name, value } = event.target;

    setSelectedReport((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveSelectedReport = async () => {
    if (!selectedReport) {
      return;
    }

    setSaveStatus({
      loading: true,
      success: '',
      error: ''
    });

    try {
      const response = await fetch(`${apiBaseUrl}/admin/reports/${selectedReport.report_id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          status: selectedReport.status,
          adminNotes: selectedReport.admin_notes || ''
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || 'Unable to save report.');
      }

      setSelectedReport((prev) => ({
        ...prev,
        ...data.report
      }));

      setReports((prev) =>
        prev.map((item) =>
          item.report_id === data.report.report_id ? { ...item, ...data.report } : item
        )
      );

      setSaveStatus({
        loading: false,
        success: 'Report updated successfully.',
        error: ''
      });
    } catch (err) {
      setSaveStatus({
        loading: false,
        success: '',
        error: err.message || 'Unable to save report.'
      });
    }
  };

  const handleTargetVisibility = async (hide) => {
    if (!selectedReport) {
      return;
    }

    setSaveStatus({
      loading: true,
      success: '',
      error: ''
    });

    try {
      const response = await fetch(
        `${apiBaseUrl}/admin/reports/${selectedReport.report_id}/hide-target`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            hide,
            hiddenReason: hide ? 'Hidden after admin moderation review' : ''
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || 'Unable to update target visibility.');
      }

      await handleSelectReport(selectedReport.report_id);

      setSaveStatus({
        loading: false,
        success: data.message || 'Target visibility updated.',
        error: ''
      });
    } catch (err) {
      setSaveStatus({
        loading: false,
        success: '',
        error: err.message || 'Unable to update target visibility.'
      });
    }
  };

  const renderTargetContent = () => {
    if (!selectedReport) {
      return null;
    }

    if (!targetContent) {
      return (
        <div className="admin-report-panel__message">
          <strong>Target Content</strong>
          <p>Target content was not found. It may have already been deleted.</p>
        </div>
      );
    }

    if (selectedReport.target_type === 'round') {
      return (
        <div className="admin-report-panel__message">
          <strong>Reported Round</strong>
          <p>
            {targetContent.first_name} {targetContent.last_name} shot{' '}
            {targetContent.total_score} at {targetContent.course_name}.
          </p>
          <p>Round ID: {targetContent.round_id}</p>
          <p>Game Date: {new Date(targetContent.game_date).toLocaleDateString()}</p>
          <p>Status: {targetContent.is_hidden ? 'Hidden' : 'Visible'}</p>
          {targetContent.hidden_reason ? <p>Reason: {targetContent.hidden_reason}</p> : null}
        </div>
      );
    }

    if (selectedReport.target_type === 'comment') {
      return (
        <div className="admin-report-panel__message">
          <strong>Reported Comment</strong>
          <p>{targetContent.comment_text}</p>
          <p>
            By: {targetContent.first_name} {targetContent.last_name}
          </p>
          <p>Status: {targetContent.is_hidden ? 'Hidden' : 'Visible'}</p>
          {targetContent.hidden_reason ? <p>Reason: {targetContent.hidden_reason}</p> : null}
        </div>
      );
    }

    if (selectedReport.target_type === 'user') {
      return (
        <div className="admin-report-panel__message">
          <strong>Reported User</strong>
          <p>
            {targetContent.first_name} {targetContent.last_name}
          </p>
          <p>{targetContent.email}</p>
          <p>
            {targetContent.city || 'No city'}, {targetContent.province || 'No province'}
          </p>
        </div>
      );
    }

    return (
      <div className="admin-report-panel__message">
        <strong>Target</strong>
        <p>Target type: {selectedReport.target_type}</p>
        <p>Target ID: {selectedReport.target_id}</p>
      </div>
    );
  };

  return (
    <div className="admin-reports-page">
      <header className="admin-reports-header">
        <div>
          <p className="admin-reports-header__eyebrow">The Tour Golfers Admin</p>
          <h1>Moderation Reports</h1>
          <p className="admin-reports-header__subtext">
            Review member reports, hide harmful content, and document moderation actions.
          </p>
        </div>

        <div className="admin-reports-header__actions">
          {adminUser ? (
            <span className="admin-reports-header__user">
              {adminUser.fullName || adminUser.email}
            </span>
          ) : null}

          <button type="button" onClick={handleLogout}>
            Log Out
          </button>
        </div>
      </header>

      <section className="admin-report-filters">
        <div className="admin-report-filters__field">
          <label htmlFor="targetType">Target Type</label>
          <select
            id="targetType"
            name="targetType"
            value={filters.targetType}
            onChange={handleFilterChange}
          >
            <option value="all">All</option>
            <option value="round">Round</option>
            <option value="comment">Comment</option>
            <option value="user">User</option>
            <option value="media">Media</option>
          </select>
        </div>

        <div className="admin-report-filters__field">
          <label htmlFor="status">Status</label>
          <select id="status" name="status" value={filters.status} onChange={handleFilterChange}>
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="reviewed">Reviewed</option>
            <option value="dismissed">Dismissed</option>
            <option value="action_taken">Action Taken</option>
          </select>
        </div>
      </section>

      {error ? <div className="admin-report-error">{error}</div> : null}

      <div className="admin-reports-layout">
        <section className="admin-reports-list">
          {loading ? (
            <div className="admin-reports-list__empty">Loading reports...</div>
          ) : reports.length === 0 ? (
            <div className="admin-reports-list__empty">No reports match the current filters.</div>
          ) : (
            reports.map((report) => (
              <button
                key={report.report_id}
                type="button"
                className={`report-list-item ${
                  selectedReport?.report_id === report.report_id ? 'is-active' : ''
                }`}
                onClick={() => handleSelectReport(report.report_id)}
              >
                <div className="report-list-item__top">
                  <span className={`report-pill report-pill--${report.target_type}`}>
                    {report.target_type}
                  </span>

                  <span className={`report-status report-status--${report.status}`}>
                    {report.status}
                  </span>
                </div>

                <h3>{report.reason.replaceAll('_', ' ')}</h3>

                <p>
                  Reporter: {report.reporter_first_name || 'Unknown'}{' '}
                  {report.reporter_last_name || ''}
                </p>

                <p>Target ID: {report.target_id}</p>

                <div className="report-list-item__bottom">
                  <span>{new Date(report.created_at).toLocaleString()}</span>
                </div>
              </button>
            ))
          )}
        </section>

        <section className="admin-report-panel">
          {panelLoading ? (
            <div className="admin-report-panel__empty">Loading report...</div>
          ) : !selectedReport ? (
            <div className="admin-report-panel__empty">Select a report to view details.</div>
          ) : (
            <>
              <div className="admin-report-panel__header">
                <div>
                  <p className="admin-report-panel__eyebrow">
                    {selectedReport.target_type} report
                  </p>
                  <h2>{selectedReport.reason.replaceAll('_', ' ')}</h2>
                </div>

                <span className={`report-status report-status--${selectedReport.status}`}>
                  {selectedReport.status}
                </span>
              </div>

              <div className="admin-report-panel__meta">
                <div>
                  <strong>Reporter</strong>
                  <span>
                    {selectedReport.reporter_first_name || 'Unknown'}{' '}
                    {selectedReport.reporter_last_name || ''}
                  </span>
                </div>

                <div>
                  <strong>Reporter Email</strong>
                  <span>{selectedReport.reporter_email || '—'}</span>
                </div>

                <div>
                  <strong>Target Type</strong>
                  <span>{selectedReport.target_type}</span>
                </div>

                <div>
                  <strong>Target ID</strong>
                  <span>{selectedReport.target_id}</span>
                </div>

                <div>
                  <strong>Submitted</strong>
                  <span>{new Date(selectedReport.created_at).toLocaleString()}</span>
                </div>

                <div>
                  <strong>Reviewed</strong>
                  <span>
                    {selectedReport.reviewed_at
                      ? new Date(selectedReport.reviewed_at).toLocaleString()
                      : 'Not reviewed'}
                  </span>
                </div>
              </div>

              <div className="admin-report-panel__message">
                <strong>Report Description</strong>
                <p>{selectedReport.description || 'No additional description provided.'}</p>
              </div>

              {renderTargetContent()}

              <div className="admin-report-panel__controls">
                <div className="admin-report-panel__field">
                  <label htmlFor="statusSelect">Status</label>
                  <select
                    id="statusSelect"
                    name="status"
                    value={selectedReport.status}
                    onChange={handleSelectedFieldChange}
                  >
                    <option value="pending">Pending</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="dismissed">Dismissed</option>
                    <option value="action_taken">Action Taken</option>
                  </select>
                </div>

                <div className="admin-report-panel__field">
                  <label htmlFor="adminNotes">Admin Notes</label>
                  <textarea
                    id="adminNotes"
                    name="admin_notes"
                    rows="6"
                    value={selectedReport.admin_notes || ''}
                    onChange={handleSelectedFieldChange}
                    placeholder="Add internal moderation notes..."
                  />
                </div>

                <div className="admin-report-actions">
                  <button
                    type="button"
                    className="admin-action-button"
                    onClick={handleSaveSelectedReport}
                    disabled={saveStatus.loading}
                  >
                    {saveStatus.loading ? 'Saving...' : 'Save Report'}
                  </button>

                  {['round', 'comment'].includes(selectedReport.target_type) ? (
                    <>
                      <button
                        type="button"
                        className="admin-action-button admin-action-button--danger"
                        onClick={() => handleTargetVisibility(true)}
                        disabled={saveStatus.loading}
                      >
                        Hide Target
                      </button>

                      <button
                        type="button"
                        className="admin-action-button admin-action-button--secondary"
                        onClick={() => handleTargetVisibility(false)}
                        disabled={saveStatus.loading}
                      >
                        Unhide Target
                      </button>
                    </>
                  ) : null}
                </div>

                {saveStatus.success ? (
                  <div className="admin-save-status admin-save-status--success">
                    {saveStatus.success}
                  </div>
                ) : null}

                {saveStatus.error ? (
                  <div className="admin-save-status admin-save-status--error">
                    {saveStatus.error}
                  </div>
                ) : null}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}